import { useState } from "react";
import { FilterType, TableHeaderType, FilterCondition, TableFilter } from "../types";
import { StoredFilterConfig } from "../contexts/datatableSlice";
import { NativeSelect, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { getOperatorOptions } from "../utils/filterConditions";
import FilterInput from "./FilterInput";

interface FilterOptionsWithOperatorProps {
  filter: FilterCondition;
  headers: TableHeaderType[];
  availableFields: {
    value: string;
    label: string;
    filterType: FilterType;
    filter?: TableFilter | StoredFilterConfig;
  }[];
  onFilterChange: (field: keyof FilterCondition, value: string | string[] | "AND" | "OR") => void;
  hasError?: boolean;
  /** Current in-dialog filter selections, used for dynamic dependent options via getOptions */
  activeDialogFilters?: FilterCondition[];
}

const FilterOptionsWithOperator = ({
  filter,
  headers,
  availableFields,
  onFilterChange,
  hasError = false,
  activeDialogFilters = [],
}: FilterOptionsWithOperatorProps) => {
  const getHeaderBySearchkey = (searchKey: string) => {
    return headers.find(h => h.filter?.searchKey === searchKey);
  };

  const fieldInfo = availableFields.find(f => f.value === filter.field);
  const header = getHeaderBySearchkey(filter.field);

  // Use header filter if available, otherwise fall back to fieldInfo.filter (for programmatic filters)
  const filterConfig = header?.filter ?? fieldInfo?.filter;

  // Get operator options using the shared utility
  const operatorOptions = header?.filter
    ? getOperatorOptions(
        header.filter.filterType,
        "filterMode" in header.filter ? header.filter.filterMode : undefined
      )
    : [{ value: "gt", label: "greater than" }];

  // Get default operator from the first option, with special handling for TEXT filters
  const getDefaultOperator = () => {
    if (filterConfig?.filterType === FilterType.TEXT && filterConfig.filterMode) {
      // For text filters with filterMode, check if it has a default value
      return typeof filterConfig.filterMode === "object"
        ? filterConfig.filterMode.value
        : filterConfig.filterMode;
    }
    // For all other cases, use the first operator option
    return operatorOptions[0].value;
  };

  // Initialize state before early return to satisfy React Hook rules
  const [selectedOperator, setSelectedOperator] = useState(getDefaultOperator());

  // Compute dynamic options if getOptions callback is provided
  const dynamicFilterConfig = (() => {
    if (!filterConfig || !("getOptions" in filterConfig) || !filterConfig.getOptions) {
      return filterConfig;
    }
    const dynamicOptions = filterConfig.getOptions(activeDialogFilters);
    return { ...filterConfig, options: dynamicOptions };
  })();

  if (!fieldInfo || !dynamicFilterConfig) {
    return null;
  }

  // Now TypeScript knows filterConfig is defined
  const headerFilter = dynamicFilterConfig;
  const disableOperator = operatorOptions.length === 1;

  // Handle operator change: update filter value with new mode if value exists
  const handleOperatorChange = (newOperator: string) => {
    setSelectedOperator(newOperator);

    // If filterMode is enabled and there's an existing value, update it with the new operator
    if (!("filterMode" in headerFilter) || !filter.value) {
      return;
    }

    // Extract the current value from filter.value (supports both string and { mode, value } formats)
    const currentValue =
      typeof filter.value === "object" && !Array.isArray(filter.value)
        ? filter.value.value
        : typeof filter.value === "string"
          ? filter.value
          : null;

    // Update the filter with the new operator if we have a valid value
    if (currentValue) {
      // Cast to string | string[] to match function signature, though FilterCondition.value also accepts { mode, value } format
      onFilterChange("value", { mode: newOperator, value: currentValue } as unknown as
        | string
        | string[]);
    }
  };

  return (
    <Stack direction="column" gap="sm" className="al-w-full">
      <Stack gap="sm" align="start">
        <NativeSelect
          value={selectedOperator}
          onChange={handleOperatorChange}
          className="al-w-32 al-text-left"
          variant="outline"
          options={operatorOptions}
          disabled={disableOperator}
          aria-label="Select operator"
        />

        <Stack direction="column" gap="none" className="al-flex-1 al-min-w-0 al-max-w-full">
          <FilterInput
            filter={filter}
            headerFilter={headerFilter}
            fieldLabel={fieldInfo.label}
            selectedOperator={selectedOperator}
            onFilterChange={onFilterChange}
          />
          {hasError && (
            <Typography className="al-text-red-500 al-text-xs al-mt-1">
              {headerFilter.filterType === FilterType.TEXT
                ? "Please enter a value for this filter"
                : "Please select a value for this filter"}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default FilterOptionsWithOperator;
